import { MEDIA_BASE } from "./mediaUpload";
import { useState, useEffect, useRef, useCallback } from "react";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

// Wikimedia Commons thumbnail helper — allows hotlinking, no API key needed
const WMC = (hash1, hash2, filename, width=400) =>
  `https://upload.wikimedia.org/wikipedia/commons/thumb/${hash1}/${hash2}/${filename}/${width}px-${filename}`;

const DEITIES = {
  surya:      { name:"Surya",      hindi:"सूर्य",     day:"Sunday",    baseHz:288, sym:"☀",   symColor:"#FFD700", cdnUrl:WMC("d","db","Surya_Narayana.jpg",400),                  grad:"linear-gradient(170deg,#7A3500 0%,#D46000 40%,#FFB830 75%,#FFF8E7 100%)", light:"#FFD080", dark:false,
    mantraAudio:`${MEDIA_BASE}/puja-audio/surya.mp3`,
    japaAudio:`${MEDIA_BASE}/puja-audio/surya_japa.mp3`,
    blessing:"सूर्य नारायण आपको तेज, यश और सफलता दें।" },
  shiva:      { name:"Shiva",      hindi:"शिव",       day:"Monday",    baseHz:220, sym:"ॐ",   symColor:"#C0A8FF", cdnUrl:WMC("7","72","Lord_Shiva_Statue_at_Murdeshwara.JPG",400),  grad:"linear-gradient(170deg,#050318 0%,#1A1060 45%,#6A5AC8 80%,#E8E4FF 100%)", light:"#C0A8FF", dark:true,
    // bg tried: AUM/Om Namah Shivaya 432Hz (too low), Maha Mrityunjaya Baritone (too loud), Shiva Gayatri, Deep Om Reverb, Raga Kanada (poor), Free Soul Om Namaha Shivaya (current-ok)
    mantraAudio:`${MEDIA_BASE}/puja-audio/shiva_japa.mp3`,
    japaAudio:`${MEDIA_BASE}/puja-audio/shiva.mp3`,
    blessing:"महादेव का आशीर्वाद — सभी बाधाएँ नष्ट हों।" },
  hanuman:    { name:"Hanuman",    hindi:"हनुमान",    day:"Tuesday",   baseHz:260, sym:"ॐ",   symColor:"#FFA060", cdnUrl:WMC("d","d3","Hanuman_-_Hindu_deity.jpg",400),              grad:"linear-gradient(170deg,#5A0800 0%,#B02800 45%,#FF6020 80%,#FFF0E8 100%)", light:"#FFA060", dark:false,
    mantraAudio:`${MEDIA_BASE}/puja-audio/hanuman.mp3`,
    japaAudio:`${MEDIA_BASE}/puja-audio/hanuman_japa.mp3`,
    blessing:"बजरंगबली आपको असीम बल, साहस और संकट-मुक्ति दें।" },
  vishnu:     { name:"Vishnu",     hindi:"विष्णु",    day:"Wednesday", baseHz:240, sym:"ॐ",   symColor:"#60E8A0", cdnUrl:WMC("e","ef","Vishnu_from_Gita_Govinda.jpg",400),           grad:"linear-gradient(170deg,#001E14 0%,#005A3C 45%,#20A870 80%,#EDFFF4 100%)", light:"#60E8A0", dark:true,
    mantraAudio:`${MEDIA_BASE}/puja-audio/vishnu.mp3`,
    japaAudio:`${MEDIA_BASE}/puja-audio/vishnu_japa.mp3`,
    blessing:"नारायण की कृपा — आपका और परिवार का सदा कल्याण हो।" },
  brihaspati: { name:"Brihaspati", hindi:"बृहस्पति", day:"Thursday",  baseHz:264, sym:"ॐ",   symColor:"#FFD700", cdnUrl:WMC("f","f0","Brihaspati_graha.JPG",400),                  grad:"linear-gradient(170deg,#3A1E00 0%,#885000 45%,#D4A017 80%,#FFFCE8 100%)", light:"#FFD700", dark:false,
    mantraAudio:`${MEDIA_BASE}/puja-audio/brihaspati.mp3`,
    // brihaspati_japa.mp3 had 1min+ intro — use surya_japa.mp3 (Free Soul Gayatri Chorus, starts immediately; Gayatri IS Brihaspati/Jupiter's mantra in Vedic tradition)
    japaAudio:`${MEDIA_BASE}/puja-audio/surya_japa.mp3`,
    blessing:"गुरु देव का आशीर्वाद — ज्ञान, बुद्धि और समृद्धि प्राप्त हो।" },
  lakshmi:    { name:"Lakshmi",    hindi:"लक्ष्मी",   day:"Friday",    baseHz:272, sym:"श्री", symColor:"#FFB0E0", cdnUrl:WMC("f","fc","Lakshmi_by_Raja_Ravi_Varma.jpg",400),         grad:"linear-gradient(170deg,#400028 0%,#A03070 45%,#E870B0 80%,#FFF0F7 100%)", light:"#FFB0E0", dark:true,
    mantraAudio:`${MEDIA_BASE}/puja-audio/lakshmi.mp3`,
    japaAudio:`${MEDIA_BASE}/puja-audio/lakshmi_japa.mp3`,
    blessing:"माँ लक्ष्मी का वास — घर में धन, सुख और प्रेम भरे।" },
  shani:      { name:"Shani",      hindi:"शनि",       day:"Saturday",  baseHz:198, sym:"ॐ",   symColor:"#A090E8", cdnUrl:WMC("e","ee","Shani_Dev.jpg",400),                         grad:"linear-gradient(170deg,#040210 0%,#180E40 45%,#4A3E88 80%,#F0EEFF 100%)", light:"#A090E8", dark:true,
    mantraAudio:`${MEDIA_BASE}/puja-audio/shani.mp3`,
    // shani_japa.mp3 = Mahamrityunjaya Mantra Chanting from Spiritual Gathering (traditional Indian group japa, widely used for Shani propitiation in Jyotish)
    japaAudio:`${MEDIA_BASE}/puja-audio/shani_japa.mp3`,
    blessing:"शनि देव न्याय और धैर्य दें — कष्ट दूर हों।" },
};
const DAY_KEY = ["surya","shiva","hanuman","vishnu","brihaspati","lakshmi","shani"];

// ─── HANUMAN CHALISA — 8 daily sections, cycles every 8 days ─────────────────
const CHALISA_SECTIONS = [
  { title:"दोहा + चौपाई १–४", verses:[
    { label:"दोहा",  text:"श्रीगुरु चरन सरोज रज, निज मनु मुकुरु सुधारि।\nबरनउँ रघुबर बिमल जसु, जो दायकु फल चारि॥" },
    { label:"१",     text:"जय हनुमान ज्ञान गुण सागर।\nजय कपीस तिहुँ लोक उजागर॥" },
    { label:"२",     text:"राम दूत अतुलित बल धामा।\nअञ्जनि पुत्र पवन सुत नामा॥" },
    { label:"३",     text:"महावीर विक्रम बजरङ्गी।\nकुमति निवार सुमति के सङ्गी॥" },
    { label:"४",     text:"कञ्चन बरन विराज सुबेसा।\nकानन कुण्डल कुञ्चित केसा॥" },
  ]},
  { title:"चौपाई ५–९", verses:[
    { label:"५",  text:"हाथ बज्र औ ध्वजा बिराजे।\nकाँधे मूँज जनेउ साजे॥" },
    { label:"६",  text:"शङ्कर सुवन केसरी नन्दन।\nतेज प्रताप महा जग वन्दन॥" },
    { label:"७",  text:"विद्यावान गुनी अति चातुर।\nराम काज करिबे को आतुर॥" },
    { label:"८",  text:"प्रभु चरित्र सुनिबे को रसिया।\nराम लखन सीता मन बसिया॥" },
    { label:"९",  text:"सूक्ष्म रूप धरि सियहिं दिखावा।\nविकट रूप धरि लङ्क जरावा॥" },
  ]},
  { title:"चौपाई १०–१४", verses:[
    { label:"१०", text:"भीम रूप धरि असुर सँहारे।\nरामचन्द्र के काज सँवारे॥" },
    { label:"११", text:"लाय सञ्जीवन लखन जियाए।\nश्री रघुवीर हरषि उर लाए॥" },
    { label:"१२", text:"रघुपति कीन्ही बहुत बड़ाई।\nतुम मम प्रिय भरतहि सम भाई॥" },
    { label:"१३", text:"सहस बदन तुम्हरो जस गावैं।\nअस कहि श्रीपति कण्ठ लगावैं॥" },
    { label:"१४", text:"सनकादिक ब्रह्मादि मुनीसा।\nनारद सारद सहित अहीसा॥" },
  ]},
  { title:"चौपाई १५–१९", verses:[
    { label:"१५", text:"जम कुबेर दिगपाल जहाँ ते।\nकवि कोविद कहि सके कहाँ ते॥" },
    { label:"१६", text:"तुम उपकार सुग्रीवहिं कीन्हा।\nराम मिलाय राजपद दीन्हा॥" },
    { label:"१७", text:"तुम्हरो मन्त्र विभीषण माना।\nलङ्केश्वर भए सब जग जाना॥" },
    { label:"१८", text:"जुग सहस्र जोजन पर भानू।\nलील्यो ताहि मधुर फल जानू॥" },
    { label:"१९", text:"प्रभु मुद्रिका मेलि मुख माहीं।\nजलधि लाँघि गए अचरज नाहीं॥" },
  ]},
  { title:"चौपाई २०–२४", verses:[
    { label:"२०", text:"दुर्गम काज जगत के जेते।\nसुगम अनुग्रह तुम्हरे तेते॥" },
    { label:"२१", text:"राम दुआरे तुम रखवारे।\nहोत न आज्ञा बिनु पैसारे॥" },
    { label:"२२", text:"सब सुख लहइ तुम्हारी सरना।\nतुम रक्षक काहू को डरना॥" },
    { label:"२३", text:"आपन तेज सम्हारो आपे।\nतीनों लोक हाँक ते काँपे॥" },
    { label:"२४", text:"भूत पिशाच निकट नहिं आवे।\nमहाबीर जब नाम सुनावे॥" },
  ]},
  { title:"चौपाई २५–२९", verses:[
    { label:"२५", text:"नासे रोग हरे सब पीरा।\nजपत निरन्तर हनुमत बीरा॥" },
    { label:"२६", text:"सङ्कट से हनुमान छुड़ावे।\nमन क्रम बचन ध्यान जो लावे॥" },
    { label:"२७", text:"सब पर राम तपस्वी राजा।\nतिनके काज सकल तुम साजा॥" },
    { label:"२८", text:"और मनोरथ जो कोई लावे।\nसोई अमित जीवन फल पावे॥" },
    { label:"२९", text:"चारों जुग परताप तुम्हारा।\nहै परसिद्ध जगत उजियारा॥" },
  ]},
  { title:"चौपाई ३०–३४", verses:[
    { label:"३०", text:"साधु सन्त के तुम रखवारे।\nअसुर निकन्दन राम दुलारे॥" },
    { label:"३१", text:"अष्ट सिद्धि नव निधि के दाता।\nअस बर दीन जानकी माता॥" },
    { label:"३२", text:"राम रसायन तुम्हरे पासा।\nसदा रहो रघुपति के दासा॥" },
    { label:"३३", text:"तुम्हरे भजन राम को पावे।\nजनम जनम के दुख बिसरावे॥" },
    { label:"३४", text:"अन्त काल रघुबर पुर जाई।\nजहाँ जन्म हरि भक्त कहाई॥" },
  ]},
  { title:"चौपाई ३५–४० + दोहा", verses:[
    { label:"३५", text:"और देवता चित्त न धरई।\nहनुमत सेई सर्व सुख करई॥" },
    { label:"३६", text:"सङ्कट कटे मिटे सब पीरा।\nजो सुमिरे हनुमत बलबीरा॥" },
    { label:"३७", text:"जय जय जय हनुमान गोसाईं।\nकृपा करहु गुरुदेव की नाईं॥" },
    { label:"३८", text:"जो सत बार पाठ कर कोई।\nछूटहि बन्दि महा सुख होई॥" },
    { label:"दोहा", text:"पवन तनय सङ्कट हरन, मङ्गल मूरति रूप।\nराम लखन सीता सहित, हृदय बसहु सुर भूप॥" },
  ]},
];

const SHOWER_MS = 60000;
const LP_CIRC   = 2 * Math.PI * 42;

const DEITY_STEPS = {
  surya:[
    { id:"aavahan", hindi:"आवाहन",     interaction:"auto",      duration:40000,mantra:"ॐ सूर्याय नमः · ॐ मित्राय नमः · ॐ रवये नमः",                            desc:"सूर्यदेव का आवाहन" },
    { id:"arghya",  hindi:"अर्घ्य",    interaction:"one-tap",   tapTarget:7,   mantra:"ॐ भास्कराय नमः · जय सूर्यनारायण",                              desc:"जल अर्पित करें", items:["💧","✨","💧","✨","💧"] },
    { id:"pushpa",  hindi:"पुष्पांजलि",interaction:"shower",                   mantra:"ॐ आदित्याय नमः · जय सूर्य भगवान",                                   desc:"पुष्प अर्पित करें", items:["🌹","🌺","🏵️","🌸","🌹","🌺","🌸","🌹"] },
    { id:"deepam",  hindi:"दीपम्",     interaction:"auto",      duration:35000,mantra:"ॐ आदित्याय विद्महे · प्रभाकराय धीमहि\nतन्नो सूर्यः प्रचोदयात्",   desc:"दीप जल रहा है" },
    { id:"gayatri", hindi:"गायत्री",   interaction:"japa",      japaTarget:7,  mantra:"ॐ भूर्भुवः स्वः\nतत्सवितुर्वरेण्यं\nभर्गो देवस्य धीमहि\nधियो यो नः प्रचोदयात्", desc:"गायत्री मंत्र" },
    { id:"pranama", hindi:"प्रणाम",    interaction:"longpress", duration:3000, mantra:"सूर्यनारायण की जय",                                                  desc:"दबाए रखें — प्रणाम करें" },
  ],
  shiva:[
    { id:"aavahan",  hindi:"आवाहन",    interaction:"auto",      duration:40000,mantra:"ॐ नमः शिवाय · ॐ नमः शिवाय · ॐ नमः शिवाय",                          desc:"महादेव का आवाहन" },
    { id:"abhishek", hindi:"जलाभिषेक", interaction:"one-tap",   tapTarget:11,  mantra:"ॐ नमः शिवाय · शिव शंकर भोलेनाथ",                             desc:"जल चढ़ाएं", items:["💧","🌊","💧","🌊","💧"] },
    { id:"bilva",    hindi:"बिल्वपत्र",interaction:"shower",                   mantra:"त्रिदलं त्रिगुणाकारं त्रिनेत्रं च त्रियायुधम्\nहर हर महादेव",     desc:"बिल्वपत्र अर्पित करें", items:["🍃","🌿","🍀","🍃","🌿","🍀","🍃"] },
    { id:"deepam",   hindi:"दीपम्",    interaction:"auto",      duration:35000,mantra:"ॐ त्र्यम्बकं यजामहे\nसुगन्धिं पुष्टिवर्धनम्\nउर्वारुकमिव बन्धनान्\nमृत्योर्मुक्षीय मामृतात्", desc:"मृत्युंजय मंत्र" },
    { id:"mantra",   hindi:"मंत्र जाप",interaction:"japa",      japaTarget:7,  mantra:"ॐ नमः शिवाय",                                                        desc:"शिव पंचाक्षरी मंत्र" },
    { id:"aarti",    hindi:"आरती",     interaction:"aarti-auto",duration:90000,mantra:"हर हर महादेव · जय शिव ओंकार · ॐ नमः शिवाय",                        desc:"शिव आरती" },
    { id:"pranama",  hindi:"प्रणाम",   interaction:"longpress", duration:3000, mantra:"जय भोलेनाथ · हर हर महादेव",                                         desc:"दबाए रखें — प्रणाम करें" },
  ],
  hanuman:[
    { id:"aavahan", hindi:"आवाहन",     interaction:"auto",      duration:40000,mantra:"जय हनुमान ज्ञान गुण सागर\nजय कपीस तिहुं लोक उजागर",              desc:"हनुमान जी का आवाहन" },
    { id:"sindoor", hindi:"सिंदूर",    interaction:"one-tap",   tapTarget:7,   mantra:"ॐ हनुमते नमः · जय बजरंगबली",                                desc:"सिंदूर अर्पित करें", items:["🔴","🔴","🔴","🔴","🔴"] },
    { id:"pushpa",  hindi:"पुष्पांजलि",interaction:"shower",                   mantra:"राम दूत अतुलित बल धामा\nअंजनि पुत्र पवनसुत नामा",                desc:"पुष्प अर्पित करें", items:["🌼","🌸","💛","🌼","🌸","🏵️","🌼","🌸"] },
    { id:"deepam",  hindi:"दीपम्",     interaction:"auto",      duration:35000,mantra:"राम राम राम · जय राम · श्री राम · जय राम",                        desc:"दीप जल रहा है" },
    { id:"mantra",  hindi:"मंत्र जाप", interaction:"japa",      japaTarget:7,  mantra:"श्री राम जय राम जय जय राम",                                         desc:"हनुमान मंत्र", note:"हनुमान जी स्वयं राम नाम जपते हैं — यही उनकी सबसे प्रिय पूजा है" },
    { id:"chalisa", hindi:"चालीसा",    interaction:"manual",                   mantra:"जय हनुमान ज्ञान गुण सागर\nजय कपीस तिहुं लोक उजागर\nराम दूत अतुलित बल धामा\nअंजनि पुत्र पवनसुत नामा", desc:"हनुमान चालीसा" },
    { id:"pranama", hindi:"प्रणाम",    interaction:"longpress", duration:3000, mantra:"जय बजरंगबली · हनुमान जी की जय",                                  desc:"दबाए रखें — प्रणाम करें" },
  ],
  vishnu:[
    { id:"aavahan",   hindi:"आवाहन",    interaction:"auto",      duration:40000,mantra:"ॐ नमो भगवते वासुदेवाय · हरि ॐ · ॐ विष्णवे नमः",               desc:"विष्णु भगवान का आवाहन" },
    { id:"tulsi",     hindi:"तुलसी",    interaction:"shower",                   mantra:"तुलसी श्रीसखी नमः · ॐ विष्णवे नमः\nहरि ॐ · नारायण नारायण",    desc:"तुलसी अर्पित करें", items:["🌿","🍃","🌱","🌿","🍃","🌿","🍃"] },
    { id:"panchamrit",hindi:"पंचामृत",  interaction:"one-tap",   tapTarget:7,   mantra:"ॐ विष्णवे नमः · ॐ नारायणाय नमः",                        desc:"पंचामृत अभिषेक", items:["🥛","💧","🥛","💧","🥛"] },
    { id:"deepam",    hindi:"दीपम्",    interaction:"auto",      duration:35000,mantra:"हरि ॐ · ॐ नमो भगवते वासुदेवाय · जय जगदीश हरे",               desc:"दीप जल रहा है" },
    { id:"mantra",    hindi:"मंत्र जाप",interaction:"japa",      japaTarget:7,  mantra:"हरे राम हरे राम\nराम राम हरे हरे\nहरे कृष्ण हरे कृष्ण\nकृष्ण कृष्ण हरे हरे",  desc:"हरे कृष्ण महामंत्र" },
    { id:"aarti",     hindi:"आरती",     interaction:"aarti-auto",duration:90000,mantra:"जय जगदीश हरे\nस्वामी जय जगदीश हरे\nभक्त जनों के संकट\nदास जनों के संकट क्षण में दूर करे", desc:"विष्णु आरती", stepAudio:`${MEDIA_BASE}/puja-audio/vishnu_aarti.mp3` },
    { id:"pranama",   hindi:"प्रणाम",   interaction:"longpress", duration:3000, mantra:"ॐ नमो भगवते वासुदेवाय · नारायण",                              desc:"दबाए रखें — प्रणाम करें" },
  ],
  brihaspati:[
    { id:"aavahan", hindi:"आवाहन",     interaction:"auto",      duration:40000,mantra:"ॐ बृहस्पतये नमः · ॐ गुरवे नमः · ॐ देवगुरवे नमः",             desc:"गुरु बृहस्पति का आवाहन" },
    { id:"pushpa",  hindi:"पीले पुष्प",interaction:"shower",                   mantra:"ॐ गुरवे नमः · ॐ देवगुरवे नमः\nगुरु कृपा ही केवलम्",          desc:"पीले पुष्प अर्पित करें", items:["🌼","🌻","💛","✨","🌼","🌻","💛","🌼"] },
    { id:"prasad",  hindi:"प्रसाद",    interaction:"one-tap",   tapTarget:7,   mantra:"ॐ ग्रां ग्रीं ग्रौं सः गुरवे नमः",                     desc:"चना दाल अर्पित करें", items:["🟡","🟡","🟡","🟡","🟡"] },
    { id:"deepam",  hindi:"दीपम्",     interaction:"auto",      duration:35000,mantra:"गुरु ब्रह्मा गुरु विष्णु\nगुरु देवो महेश्वरः\nगुरु साक्षात् परब्रह्म\nतस्मै श्री गुरवे नमः", desc:"गुरु मंत्र" },
    { id:"mantra",  hindi:"गुरु मंत्र",interaction:"japa",      japaTarget:7,  mantra:"ॐ अंगिरसाय विद्महे\nदिव्य देहाय धीमहि\nतन्नो जीवः प्रचोदयात्",  desc:"बृहस्पति गायत्री मंत्र जाप" },
    { id:"pranama", hindi:"प्रणाम",    interaction:"longpress", duration:3000, mantra:"बृहस्पति देव की जय",                                          desc:"दबाए रखें — प्रणाम करें" },
  ],
  lakshmi:[
    { id:"aavahan", hindi:"आवाहन",     interaction:"auto",      duration:40000,mantra:"ॐ श्री महालक्ष्म्यै नमः · ॐ ह्रीं श्रीं · जय लक्ष्मी माता", desc:"महालक्ष्मी का आवाहन" },
    { id:"kamal",   hindi:"कमल पुष्प", interaction:"shower",                   mantra:"ॐ श्रीं ह्रीं श्रीं · ॐ श्रियै नमः\nजय लक्ष्मी माता",        desc:"कमल पुष्प अर्पित करें", items:["🪷","🌸","💗","🪷","🌸","🌹","🪷","💗"] },
    { id:"kumkum",  hindi:"कुमकुम",    interaction:"one-tap",   tapTarget:7,   mantra:"ॐ ह्रीं श्रीं लक्ष्मीभ्यो नमः",                       desc:"कुमकुम अर्पित करें", items:["❤️","🔴","❤️","🔴","❤️"] },
    { id:"deepam",  hindi:"दीपम्",     interaction:"auto",      duration:35000,mantra:"हिरण्यवर्णाम् हरिणीम्\nसुवर्णरजतस्रजाम्\nचन्द्राम् हिरण्मयीम् लक्ष्मीम्\nजातवेदो म आवह", desc:"श्री सूक्तम्" },
    { id:"mantra",  hindi:"मंत्र जाप", interaction:"japa",      japaTarget:7,  mantra:"ॐ श्रीं महालक्ष्म्यै नमः",  desc:"लक्ष्मी बीज मंत्र जाप" },
    { id:"aarti",   hindi:"आरती",      interaction:"aarti-auto",duration:90000,mantra:"जय लक्ष्मी माता\nमैया जय लक्ष्मी माता\nतुमको निशदिन ध्यावत\nहरि विष्णु विधाता", desc:"लक्ष्मी आरती" },
    { id:"pranama", hindi:"प्रणाम",    interaction:"longpress", duration:3000, mantra:"लक्ष्मी माता की जय · श्री महालक्ष्मी की जय",               desc:"दबाए रखें — प्रणाम करें" },
  ],
  shani:[
    { id:"aavahan", hindi:"आवाहन",    interaction:"auto",      duration:40000,mantra:"ॐ शं शनैश्चराय नमः · ॐ प्रां प्रीं प्रौं सः शनैश्चराय नमः",  desc:"शनि देव का आवाहन" },
    { id:"til",     hindi:"काले तिल", interaction:"shower",                   mantra:"ॐ प्रां प्रीं प्रौं सः शनैश्चराय नमः\nनीलाञ्जन समाभासम्",    desc:"काले तिल अर्पित करें", items:["⚫","◾","⚫","🖤","◾","⚫","🖤","⚫"] },
    { id:"tel",     hindi:"तेल दीप",  interaction:"one-tap",   tapTarget:7,   mantra:"नीलाञ्जन समाभासम् · रविपुत्रं यमाग्रजम्",            desc:"तेल दीपक जलाएं", items:["🕯️","💧","🕯️","💧","🕯️"] },
    { id:"deepam",  hindi:"दीपम्",    interaction:"auto",      duration:35000,mantra:"नीलाञ्जन समाभासम्\nरविपुत्रं यमाग्रजम्\nछायामार्तण्डसम्भवम्\nतं नमामि शनैश्चरम्", desc:"शनि स्तोत्र" },
    { id:"mantra",  hindi:"मंत्र जाप",interaction:"japa",      japaTarget:7,  mantra:"ॐ शं शनैश्चराय नमः",                                                 desc:"शनि बीज मंत्र जाप" },
    { id:"aarti",   hindi:"आरती",     interaction:"aarti-auto",duration:90000,mantra:"शनि देव की जय · ॐ शं · शनि महाराज की जय",                   desc:"शनि आरती" },
    { id:"pranama", hindi:"प्रणाम",   interaction:"longpress", duration:3000, mantra:"शनि महाराज की जय · ॐ शं शनैश्चराय नमः",                     desc:"दबाए रखें — प्रणाम करें" },
  ],
};

// ─── CSS ──────────────────────────────────────────────────────────────────────
const CSS = `
@keyframes vpFlame  { 0%,100%{transform:scaleY(1) rotate(-2deg)} 50%{transform:scaleY(1.18) rotate(2.5deg)} }
@keyframes vpBreath { 0%,100%{transform:scale(1)} 50%{transform:scale(1.05)} }
@keyframes vpFallN  { 0%{opacity:1;transform:translateY(0) translateX(0) rotate(0deg) scale(var(--fs,1))} 45%{opacity:0.95;transform:translateY(160px) translateX(var(--dx,18px)) rotate(230deg) scale(calc(var(--fs,1)*0.8))} 100%{opacity:0;transform:translateY(400px) translateX(calc(var(--dx,18px)*2.4)) rotate(540deg) scale(0.15)} }
@keyframes vpFadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
@keyframes vpSmoke  { 0%{transform:translateY(0) scale(1);opacity:0.35} 100%{transform:translateY(-85px) translateX(var(--sx,5px)) scale(3);opacity:0} }
@keyframes vpAarti  { 0%{transform:rotate(0deg) translateX(72px) rotate(0deg)} 100%{transform:rotate(360deg) translateX(72px) rotate(-360deg)} }
@keyframes vpPulse  { 0%,100%{transform:scale(1);opacity:0.7} 50%{transform:scale(1.14);opacity:1} }
@keyframes vpFrameR { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
@keyframes vpGlowR  { 0%,100%{box-shadow:0 0 20px var(--gl),0 0 40px var(--gl)} 50%{box-shadow:0 0 50px var(--gl),0 0 100px var(--gl)} }
@keyframes vpShine  { 0%,100%{filter:brightness(1)} 50%{filter:brightness(1.22) saturate(1.3)} }
@keyframes vpDeepR0 { 0%{transform:rotate(0deg)   translateX(60px) rotate(0deg)}   100%{transform:rotate(360deg)   translateX(60px) rotate(-360deg)} }
@keyframes vpDeepR1 { 0%{transform:rotate(180deg)  translateX(60px) rotate(-180deg)} 100%{transform:rotate(540deg)  translateX(60px) rotate(-540deg)} }
@keyframes vpAartiOrb { 0%{transform:rotate(0deg) translateX(95px) rotate(0deg)} 100%{transform:rotate(360deg) translateX(95px) rotate(-360deg)} }
@keyframes vpDivineRay { 0%,100%{opacity:0.08;transform:scale(1)} 50%{opacity:0.18;transform:scale(1.08)} }
@keyframes vpBell   { 0%,100%{transform:rotate(0deg)} 20%{transform:rotate(16deg)} 50%{transform:rotate(-13deg)} 80%{transform:rotate(7deg)} }
@keyframes vpTorana { 0%,100%{filter:brightness(1) drop-shadow(0 0 6px rgba(255,200,0,0.6))} 50%{filter:brightness(1.2) drop-shadow(0 0 14px rgba(255,200,0,0.9))} }
.vp-flame  { animation:vpFlame 0.65s ease-in-out infinite; transform-origin:bottom center; display:inline-block; }
.vp-breath { animation:vpBreath 3.5s ease-in-out infinite; }
.vp-pulse  { animation:vpPulse 1.8s ease-in-out infinite; }
.vp-shine  { animation:vpShine 2.5s ease-in-out infinite; }
.vp-fadein { animation:vpFadeUp 0.45s ease-out; }
.vp-glow   { animation:vpGlowR 2.5s ease-in-out infinite; border-radius:50%; display:inline-block; }
.vp-item-n { position:fixed; pointer-events:none; animation:vpFallN 4.4s ease-in forwards; z-index:20; }
.vp-aarti-flame { position:absolute; font-size:28px; animation:vpAarti 3s linear infinite; transform-origin:0 0; filter:drop-shadow(0 0 10px #FF8C00); }
.vp-frame-spin  { animation:vpFrameR 25s linear infinite; transform-origin:center; }
.vp-bell   { animation:vpBell 2.4s ease-in-out infinite; transform-origin:top center; display:inline-block; }
.vp-torana { animation:vpTorana 2s ease-in-out infinite; }
`;

// ─── AUDIO ────────────────────────────────────────────────────────────────────
let _ac = null;
const getAC = () => { if (!_ac) _ac = new (window.AudioContext||window.webkitAudioContext)(); return _ac; };

function playBell(vol=0.4) {
  try {
    const ctx=getAC(), t=ctx.currentTime;
    [[440,vol,3.8],[1213,vol*0.42,2.6],[2376,vol*0.18,1.8],[3933,vol*0.06,0.9]].forEach(([f,v,d])=>{
      const o=ctx.createOscillator(), g=ctx.createGain();
      o.connect(g); g.connect(ctx.destination); o.type="sine"; o.frequency.value=f;
      g.gain.setValueAtTime(0,t); g.gain.linearRampToValueAtTime(v,t+0.005);
      g.gain.exponentialRampToValueAtTime(0.0001,t+d); o.start(t); o.stop(t+d);
    });
    const oe=ctx.createOscillator(), ge=ctx.createGain();
    oe.connect(ge); ge.connect(ctx.destination); oe.type="sine"; oe.frequency.value=439.1;
    ge.gain.setValueAtTime(0,t+0.08); ge.gain.linearRampToValueAtTime(vol*0.25,t+0.085);
    ge.gain.exponentialRampToValueAtTime(0.0001,t+2.5); oe.start(t+0.08); oe.stop(t+2.8);
  } catch(e) {}
}

function playWhoosh() {
  try {
    const ctx=getAC(), t=ctx.currentTime;
    const buf=ctx.createBuffer(1, Math.floor(ctx.sampleRate*0.25), ctx.sampleRate);
    const d=buf.getChannelData(0);
    for(let i=0;i<d.length;i++) d[i]=(Math.random()*2-1)*(1-i/d.length)*0.6;
    const src=ctx.createBufferSource(), f=ctx.createBiquadFilter(), g=ctx.createGain();
    f.type="bandpass"; f.frequency.value=1600; f.Q.value=0.6;
    src.buffer=buf; src.connect(f); f.connect(g); g.connect(ctx.destination);
    g.gain.setValueAtTime(0.18,t); g.gain.exponentialRampToValueAtTime(0.0001,t+0.25); src.start(t);
  } catch(e) {}
}

// Shankh (conch) blow — plays once at puja start, authentic and non-looping
function playShankh() {
  try {
    const ctx = getAC(), t = ctx.currentTime;
    const osc  = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const lp   = ctx.createBiquadFilter();
    const gain = ctx.createGain();
    // Sawtooth filtered to give conch-horn timbre
    osc.type  = "sawtooth";
    osc2.type = "sine";
    // Frequency arc: starts mid, rises slightly, then descends — characteristic shankh sweep
    osc.frequency.setValueAtTime(360, t);
    osc.frequency.linearRampToValueAtTime(430, t+0.6);
    osc.frequency.exponentialRampToValueAtTime(200, t+3.8);
    osc2.frequency.setValueAtTime(720, t);
    osc2.frequency.exponentialRampToValueAtTime(400, t+3.8);
    lp.type = "lowpass"; lp.frequency.value = 1400; lp.Q.value = 3;
    const g2 = ctx.createGain(); g2.gain.value = 0.25;
    osc.connect(lp); osc2.connect(g2); g2.connect(lp);
    lp.connect(gain); gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.32, t+0.25);
    gain.gain.setValueAtTime(0.32, t+2.8);
    gain.gain.exponentialRampToValueAtTime(0.0001, t+4.2);
    osc.start(t); osc.stop(t+4.5);
    osc2.start(t); osc2.stop(t+4.5);
  } catch(e) {}
}

// Mantra chime — ascending Sa-Ma-Pa-Sa played once on step start
function playMantraChime(baseHz=220) {
  try {
    const ctx = getAC(), t = ctx.currentTime;
    [baseHz, baseHz*4/3, baseHz*3/2, baseHz*2].forEach((f, i) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "sine"; o.frequency.value = f;
      o.connect(g); g.connect(ctx.destination);
      const s = t + i*0.32;
      g.gain.setValueAtTime(0, s);
      g.gain.linearRampToValueAtTime(0.18/(i*0.4+1), s+0.06);
      g.gain.exponentialRampToValueAtTime(0.0001, s+2.2);
      o.start(s); o.stop(s+2.3);
    });
  } catch(e) {}
}


// ─── DEITY PORTRAIT — local file → CDN → symbol ──────────────
function DeityPortrait({ deityKey, size=200, glowing=false }) {
  // 0 = local /images/deities/{key}.jpg, 1 = Wikimedia CDN, 2 = symbol
  const [imgStage, setImgStage] = useState(0);
  const d = DEITIES[deityKey];
  const half = size/2;

  const imgSrc = imgStage === 0 ? `/images/deities/${deityKey}.jpg` : d.cdnUrl;

  return (
    <div style={{position:"relative", width:size, height:size, display:"inline-block"}}>
      {/* Main image or symbol */}
      {imgStage < 2 ? (
        <img
          src={imgSrc}
          alt={d.name}
          onError={() => setImgStage(s => s + 1)}
          className={glowing ? "vp-shine" : "vp-breath"}
          style={{
            width:size, height:size, borderRadius:"50%",
            objectFit:"cover", objectPosition:"top center", display:"block",
          }}
        />
      ) : (
        <DeitySymbol deityKey={deityKey} size={size} glowing={glowing}/>
      )}

      {/* Ornate rotating frame overlay */}
      <svg style={{position:"absolute",top:0,left:0,pointerEvents:"none"}}
        width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Outer rotating lotus ring */}
        <g className="vp-frame-spin" style={{transformOrigin:`${half}px ${half}px`}}>
          {Array.from({length:16}, (_,i) => {
            const a = (i*22.5)*Math.PI/180;
            const r = size*0.48;
            const px = half + r*Math.cos(a), py = half + r*Math.sin(a);
            return <ellipse key={i} cx={px} cy={py}
              rx={size*0.033} ry={size*0.082}
              fill={glowing ? `rgba(255,215,0,0.85)` : `rgba(255,215,0,0.45)`}
              transform={`rotate(${i*22.5+90},${px},${py})`}/>;
          })}
        </g>
        {/* Gold border ring */}
        <circle cx={half} cy={half} r={size*0.445} fill="none"
          stroke={glowing ? "rgba(255,215,0,0.95)" : "rgba(255,215,0,0.6)"}
          strokeWidth={size*0.022}/>
        {/* 4 gem dots at cardinal points */}
        {[0,90,180,270].map(deg => {
          const a=deg*Math.PI/180;
          return <circle key={deg}
            cx={half + size*0.445*Math.cos(a)} cy={half + size*0.445*Math.sin(a)}
            r={size*0.022} fill={d.light}/>;
        })}
        {/* Inner glow ring when active */}
        {glowing && <circle cx={half} cy={half} r={size*0.40} fill="none"
          stroke={d.light} strokeWidth={size*0.012} strokeOpacity="0.5"/>}
      </svg>
    </div>
  );
}

// ─── DEITY SYMBOL (fallback — traditional yantra style, no cartoon) ───────────
function DeitySymbol({ deityKey, size, glowing }) {
  const d = DEITIES[deityKey];
  const half = size/2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
      className={glowing ? "vp-shine" : "vp-breath"}>
      {/* Deep background */}
      <circle cx={half} cy={half} r={size*0.44} fill="rgba(0,0,0,0.45)"/>
      {/* 8 inner lotus petals */}
      {Array.from({length:8}, (_,i) => {
        const a = (i*45)*Math.PI/180;
        const r = size*0.26;
        const px = half+r*Math.cos(a), py = half+r*Math.sin(a);
        return <ellipse key={i} cx={px} cy={py}
          rx={size*0.055} ry={size*0.13}
          fill={d.symColor} fillOpacity="0.30"
          transform={`rotate(${i*45},${px},${py})`}/>;
      })}
      {/* Sacred symbol */}
      <text x={half} y={half + size*0.12} textAnchor="middle"
        fontSize={size*0.48} fill={d.symColor}
        fontFamily="serif" fontWeight="bold"
        style={{filter:`drop-shadow(0 0 ${size*0.05}px ${d.symColor})`}}>
        {d.sym}
      </text>
      {/* Deity name below */}
      <text x={half} y={half + size*0.36} textAnchor="middle"
        fontSize={size*0.092} fill={d.symColor}
        fontFamily="sans-serif" fontWeight="700" letterSpacing="1">
        {d.hindi}
      </text>
    </svg>
  );
}

// ─── INCENSE SMOKE ────────────────────────────────────────────────────────────
function IncenseSmoke({ color }) {
  return (
    <div style={{position:"fixed",bottom:"10%",right:"7%",pointerEvents:"none",zIndex:1}}>
      {Array.from({length:7},(_,i)=>(
        <div key={i} style={{position:"absolute",width:4,height:4,borderRadius:"50%",
          background:color,bottom:`${i*14}px`,left:`${i%2===0?0:6}px`,
          animation:`vpSmoke ${2+i*0.4}s ease-out ${i*0.45}s infinite`,
          "--sx":`${i%2===0?5:-5}px`}}/>
      ))}
    </div>
  );
}

// ─── AARTI RING — fire-cluster thali orbiting fast at 95px ───────────────────
// Visually distinct from DeepamWave: uses 🔥 cluster (not 🪔 lamps), big radius, fast
function AartiRing() {
  // 5-flame cluster positions on the "thali"
  const flames = [
    {dx:-14,dy:-12,sz:26},{dx:0,dy:-18,sz:30},{dx:14,dy:-12,sz:26},
    {dx:-8, dy:2,  sz:20},{dx:8, dy:2,  sz:20},
  ];
  return (
    <div style={{position:"relative",width:240,height:240,margin:"0 auto"}}>
      {/* Primary aarti thali — 5 flames orbiting fast */}
      {[2.4, 3.0, 3.8].map((dur, gi) => (
        <div key={gi} style={{
          position:"absolute",left:"50%",top:"50%",
          animation:`vpAartiOrb ${dur}s linear infinite`,
          animationDelay:`${gi*-0.9}s`,
          transformOrigin:"0 0",
        }}>
          {gi === 0
            ? flames.map((f,i) => (
              <div key={i} style={{position:"absolute",left:f.dx,top:f.dy,fontSize:f.sz,
                filter:"drop-shadow(0 0 12px #FF5500) drop-shadow(0 0 5px #FFD700)"}}>
                <span className="vp-flame">🔥</span>
              </div>
            ))
            : <div style={{fontSize:gi===1?28:22,filter:"drop-shadow(0 0 10px #FF6600)"}}>
                <span className="vp-flame">🔥</span>
              </div>
          }
        </div>
      ))}
      {/* Center aarti glow */}
      <div style={{position:"absolute",left:"50%",top:"50%",transform:"translate(-50%,-50%)",
        fontSize:26,filter:"drop-shadow(0 0 24px #FFD700) drop-shadow(0 0 10px #FF8C00)"}}>
        <span className="vp-pulse">✨</span>
      </div>
    </div>
  );
}

// ─── DEEPAM WAVE (rotating lamp in front of deity) ────────────────────────────
function DeepamWave() {
  return (
    <div style={{position:"relative",width:160,height:160,margin:"0 auto"}}>
      {/* Center diya — static with breathing flame */}
      <div style={{position:"absolute",left:"50%",top:"50%",transform:"translate(-50%,-50%)",
        fontSize:34,filter:"drop-shadow(0 0 18px #FF8C00) drop-shadow(0 0 6px #FFD700)"}}>
        <span className="vp-flame">🔥</span>
      </div>
      {/* 2 rotating lamps at different speeds */}
      {[0,1].map(i => (
        <div key={i} style={{
          position:"absolute", left:"50%", top:"50%",
          fontSize: i===0 ? 30 : 24,
          animation:`vpDeepR${i} ${i===0?3.2:4.8}s linear infinite`,
          transformOrigin:"0 0",
          filter:"drop-shadow(0 0 12px #FF8C00) drop-shadow(0 0 4px #FFD700)",
        }}>🪔</div>
      ))}
    </div>
  );
}

// ─── CHALISA READER — shows today's section, advances daily ──────────────────
function ChalisaSection({ textCol }) {
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(),0,0)) / 86400000);
  const sec = CHALISA_SECTIONS[dayOfYear % CHALISA_SECTIONS.length];
  return (
    <div style={{
      background:"rgba(0,0,0,0.25)", backdropFilter:"blur(8px)",
      borderRadius:16, padding:"14px 16px", marginTop:4,
      border:"1px solid rgba(255,255,255,0.18)",
      maxHeight:260, overflowY:"auto",
    }}>
      <div style={{fontSize:11,letterSpacing:3,textTransform:"uppercase",
        color:"rgba(255,200,80,0.9)",fontWeight:700,marginBottom:10,textAlign:"center"}}>
        📖 हनुमान चालीसा · {sec.title}
      </div>
      {sec.verses.map((v, i) => (
        <div key={i} style={{marginBottom:12,textAlign:"center"}}>
          <div style={{fontSize:10,color:"rgba(255,200,80,0.7)",marginBottom:3,letterSpacing:1}}>
            {v.label}
          </div>
          <div style={{fontSize:15,lineHeight:1.75,fontWeight:600,color:textCol,
            fontFamily:"serif"}}>
            {v.text.split("\n").map((line,li) => (
              <div key={li}>{line}</div>
            ))}
          </div>
        </div>
      ))}
      <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",textAlign:"center",marginTop:8}}>
        कल अगला भाग · {CHALISA_SECTIONS[(dayOfYear+1) % CHALISA_SECTIONS.length].title}
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function VirtualPujaPage({ user }) {
  const [phase,      setPhase]     = useState("intro");
  const [stepIdx,    setStepIdx]   = useState(0);
  const [items,      setItems]     = useState([]);
  const [lpPct,      setLpPct]     = useState(0);
  const [glowing,    setGlowing]   = useState(false);
  const [mantraLine, setMLine]     = useState(0);
  const [surrender,  setSurrender] = useState("");
  const [saving,     setSaving]    = useState(false);
  const [history,    setHistory]   = useState([]);
  const [deityKey,   setDeityKey]  = useState(() => DAY_KEY[new Date().getDay()]);
  const [done,       setDone]      = useState(false);
  const [secsLeft,   setSecsLeft]  = useState(0);
  const [sankalpa,   setSankalpa]  = useState("");
  const [tapCount,   setTapCount]  = useState(0);
  const [japaTarget, setJapaTarget]= useState(7);
  const [muted,      setMuted]     = useState(false);

  const lpRafRef    = useRef(null);
  const autoTimRef  = useRef(null);
  const mantraRef   = useRef(null);
  const guardRef    = useRef(false);
  const itemId      = useRef(0);
  const waveRef     = useRef(null);
  const audioRef    = useRef(null);
  const tapCountRef = useRef(0);

  const steps = DEITY_STEPS[deityKey];
  const step  = steps[stepIdx];
  const deity = DEITIES[deityKey];
  const textCol = deity.dark ? "#F0ECFF" : "#2A1200";

  useEffect(() => {
    if (document.getElementById("vp-css")) return;
    const s = document.createElement("style"); s.id = "vp-css";
    s.textContent = CSS; document.head.appendChild(s);
  }, []);

  useEffect(() => {
    if (!user?.id || phase !== "intro") return;
    fetch(`${API}/virtual-puja/history/${user.id}`).then(r=>r.json()).then(setHistory).catch(()=>{});
  }, [user, phase]);

  // Audio per step: stepAudio > japaAudio (japa) > mantraAudio (default)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (phase === "puja") {
      const isJapa = step?.interaction === "japa";
      const deity = DEITIES[deityKey];
      const src = step?.stepAudio
        || (isJapa ? deity?.japaAudio : null)
        || deity?.mantraAudio
        || "";
      audio.src = src;
      audio.volume = isJapa ? 0.32 : 0.28;
      audio.loop = true;
      audio.muted = muted;
      audio.play().catch(() => {});
    } else {
      audio.pause();
      audio.src = "";
    }
  }, [phase, deityKey, stepIdx]); // eslint-disable-line

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) audio.muted = muted;
  }, [muted]);

  // no persistent audio nodes to clean up

  // Mantra line cycling
  useEffect(() => {
    if (phase !== "puja") return;
    const lines = step.mantra.split("\n");
    if (lines.length < 2) { setMLine(0); return; }
    setMLine(0);
    const interval = step.interaction === "shower" ? 5000 : Math.max((step.duration||6000) / lines.length, 4000);
    mantraRef.current = setInterval(() => setMLine(l => (l+1) % lines.length), interval);
    return () => clearInterval(mantraRef.current);
  }, [phase, stepIdx, deityKey]); // eslint-disable-line

  const advanceStep = useCallback((ringBell=false) => {
    if (guardRef.current) return;
    guardRef.current = true;
    if (ringBell) playBell();
    setGlowing(true);
    clearTimeout(autoTimRef.current);
    clearInterval(waveRef.current);
    if (lpRafRef.current) cancelAnimationFrame(lpRafRef.current);
    clearInterval(mantraRef.current);
    setTimeout(() => {
      setGlowing(false); setItems([]); setLpPct(0); setDone(false); setSecsLeft(0);
      setTapCount(0); tapCountRef.current = 0;
      guardRef.current = false;
      if (stepIdx < steps.length-1) setStepIdx(i=>i+1); else setPhase("surrender");
    }, 700);
  }, [stepIdx, steps]);

  // Single soft bell on step change (not chime — too jarring on mobile)
  useEffect(() => {
    if (phase !== "puja" || stepIdx === 0) return;
    playBell(0.18); // soft bell marking step transition
  }, [phase, stepIdx, deityKey]); // eslint-disable-line

  // Auto steps
  useEffect(() => {
    if (phase !== "puja" || !["auto","aarti-auto"].includes(step?.interaction)) return;
    autoTimRef.current = setTimeout(advanceStep, step.duration);
    return () => clearTimeout(autoTimRef.current);
  }, [phase, stepIdx, deityKey]); // eslint-disable-line

  // Long press
  const startLP = useCallback((e) => {
    e.preventDefault(); if (guardRef.current) return;
    const start=Date.now(), dur=step.duration||3000;
    const tick=()=>{ const p=Math.min(((Date.now()-start)/dur)*100,100); setLpPct(p); if(p>=100) advanceStep(true); else lpRafRef.current=requestAnimationFrame(tick); };
    lpRafRef.current = requestAnimationFrame(tick);
  }, [step, advanceStep]);
  const endLP = useCallback(() => { if(lpRafRef.current) cancelAnimationFrame(lpRafRef.current); setLpPct(0); }, []);

  // Spawn helper — natural drift variation per flower
  const spawnWave = useCallback((emojis, count=12) => {
    const cx  = window.innerWidth/2;
    const now = Date.now();
    const fresh = Array.from({length:count}, () => ({
      id:    itemId.current++,
      x:     cx + (Math.random()-0.5)*340 - 18,
      y:     -30 + Math.random()*50,          // start above viewport
      e:     emojis[Math.floor(Math.random()*emojis.length)],
      delay: Math.random()*0.9,
      dx:    (Math.random()-0.5)*90,           // horizontal wind drift
      sz:    18 + Math.floor(Math.random()*18),// size variety 18-36px
      born:  now,
    }));
    setItems(prev => [
      ...prev.filter(f => now - f.born < 4400),
      ...fresh,
    ]);
  }, []);

  // 60-second continuous shower
  const startShower = useCallback(() => {
    if (done || guardRef.current) return;
    setDone(true);
    playBell(0.22);
    const endTime = Date.now() + SHOWER_MS;
    const emojis = step.items || ["🌸","🌼","🌺"];
    const tick = () => {
      const rem = endTime - Date.now();
      if (rem <= 0) { advanceStep(); return; }
      setSecsLeft(Math.ceil(rem/1000));
      spawnWave(emojis);
    };
    tick();
    waveRef.current = setInterval(tick, 1400);
  }, [done, step, deityKey, advanceStep, spawnWave]);

  const handleJapa = useCallback(() => {
    if (guardRef.current) return;
    tapCountRef.current += 1;
    setTapCount(tapCountRef.current);
    playBell(0.15);
    if (tapCountRef.current >= japaTarget) {
      playBell(0.38);
      setTimeout(() => advanceStep(false), 900);
    }
  }, [japaTarget, advanceStep]);

  const handleOneTap = useCallback(() => {
    if (guardRef.current) return;
    const target = step.tapTarget || 7;
    tapCountRef.current += 1;
    setTapCount(tapCountRef.current);
    spawnWave(step.items || ["💧"], 4);
    playBell(0.15);
    if (tapCountRef.current >= target) advanceStep(true);
  }, [step, advanceStep, spawnWave]);

  const handleTap = useCallback((e) => {
    if (phase !== "puja" || guardRef.current || done) return;
    if (step.interaction === "shower") startShower();
    else if (["auto","aarti-auto"].includes(step.interaction)) { clearTimeout(autoTimRef.current); advanceStep(true); }
  }, [phase, step, done, startShower, advanceStep]);

  const savePuja = async () => {
    if (!user?.id) { setPhase("complete"); return; }
    setSaving(true);
    try {
      await fetch(`${API}/virtual-puja/complete`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({user_id:user.id, deity_key:deityKey, surrender_text:surrender}),
      });
    } catch(e) {}
    setSaving(false); setPhase("complete");
  };

  const startPuja = (key) => {
    setDeityKey(key); setPhase("sankalpa"); setStepIdx(0);
    setItems([]); setLpPct(0); setGlowing(false); setDone(false); setSecsLeft(0);
    setTapCount(0); tapCountRef.current = 0; setSankalpa(""); setJapaTarget(7);
    guardRef.current = false;
  };

  const beginPuja = () => {
    playBell(0.3);
    setPhase("puja");
  };

  const bg = {
    background: deity.grad, minHeight:"100vh", width:"100%",
    position:"relative", overflow:"hidden",
    userSelect:"none", WebkitUserSelect:"none", WebkitTapHighlightColor:"transparent",
  };

  const mantraLines = step?.mantra?.split("\n") || [];
  const currentMantra = mantraLines.length > 1 ? mantraLines[mantraLine % mantraLines.length] : step?.mantra || "";

  // ─── INTRO ────────────────────────────────────────────────────────────────
  if (phase === "intro") return (
    <div style={bg}>
      <IncenseSmoke color={deity.light}/>
      <div style={{maxWidth:440,margin:"0 auto",padding:"24px 20px",textAlign:"center"}}>
        <div className="vp-glow" style={{"--gl":deity.light,marginBottom:10}}>
          <DeityPortrait deityKey={deityKey} size={200} glowing/>
        </div>
        <h2 style={{fontSize:28,fontWeight:800,color:textCol,margin:"0 0 2px",letterSpacing:1}}>{deity.hindi}</h2>
        <p style={{color:textCol,opacity:0.65,fontSize:14,margin:"0 0 6px"}}>{deity.name} · {deity.day}</p>
        {history.length > 0 && <p style={{fontSize:12,color:textCol,opacity:0.5,margin:"0 0 10px"}}>{history.length} पूजा पूर्ण ✓</p>}
        <p style={{fontSize:14,color:textCol,opacity:0.75,margin:"0 0 22px",lineHeight:1.7}}>
          {steps.map(s=>s.hindi).join(" · ")}
        </p>
        <button onClick={() => startPuja(deityKey)}
          style={{background:"rgba(255,255,255,0.18)",backdropFilter:"blur(8px)",color:textCol,
            border:"1.5px solid rgba(255,255,255,0.4)",borderRadius:28,padding:"14px 40px",
            fontSize:18,fontWeight:700,cursor:"pointer",letterSpacing:0.5}}>
          🪔 पूजा शुरू करें
        </button>
        <div style={{marginTop:22,display:"flex",gap:7,justifyContent:"center",flexWrap:"wrap"}}>
          {Object.entries(DEITIES).map(([k,d]) => (
            <button key={k} onClick={() => setDeityKey(k)}
              style={{border:`2px solid ${k===deityKey?"rgba(255,255,255,0.85)":"rgba(255,255,255,0.2)"}`,
                borderRadius:20,padding:"5px 12px",
                background:k===deityKey?"rgba(255,255,255,0.22)":"rgba(255,255,255,0.07)",
                color:textCol,fontSize:13,cursor:"pointer",fontWeight:k===deityKey?700:400}}>
              {d.hindi}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  if (phase === "sankalpa") return (
    <div style={bg}>
      <IncenseSmoke color={deity.light}/>
      <div style={{maxWidth:440,margin:"0 auto",padding:"28px 20px",textAlign:"center"}}>
        <div style={{fontSize:11,letterSpacing:4,color:textCol,opacity:0.5,textTransform:"uppercase",marginBottom:14}}>संकल्प</div>
        <DeityPortrait deityKey={deityKey} size={110} glowing/>
        <h3 style={{color:textCol,fontSize:18,margin:"12px 0 4px"}}>आज की पूजा किस लिए?</h3>
        <p style={{color:textCol,opacity:0.55,fontSize:13,margin:"0 0 16px",lineHeight:1.6}}>
          अपना संकल्प बताएं — {deity.hindi} सुन रहे हैं
        </p>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"center",marginBottom:14}}>
          {["परीक्षा सफलता","नौकरी / Interview","स्वास्थ्य","परिवार की रक्षा","मनोकामना पूर्ति"].map(p => (
            <button key={p} onClick={()=>setSankalpa(p)}
              style={{background:sankalpa===p?"rgba(255,255,255,0.38)":"rgba(255,255,255,0.13)",
                border:`1.5px solid ${sankalpa===p?"rgba(255,255,255,0.8)":"rgba(255,255,255,0.28)"}`,
                color:textCol,borderRadius:20,padding:"7px 14px",fontSize:13,
                cursor:"pointer",fontWeight:sankalpa===p?700:400}}>
              {p}
            </button>
          ))}
        </div>
        <textarea value={sankalpa} onChange={e=>setSankalpa(e.target.value)}
          placeholder="या यहाँ लिखें…" rows={2}
          style={{width:"100%",border:"1.5px solid rgba(255,255,255,0.28)",borderRadius:12,
            padding:"10px 14px",fontSize:14,background:"rgba(255,255,255,0.12)",color:textCol,
            resize:"none",outline:"none",boxSizing:"border-box",fontFamily:"inherit",
            backdropFilter:"blur(6px)",marginBottom:16}}/>
        <button onClick={beginPuja}
          style={{background:"rgba(255,255,255,0.2)",backdropFilter:"blur(6px)",color:textCol,
            border:"1.5px solid rgba(255,255,255,0.45)",borderRadius:28,padding:"14px 0",
            fontSize:18,fontWeight:700,cursor:"pointer",width:"100%",marginBottom:10}}>
          🪔 पूजा शुरू करें
        </button>
        <button onClick={beginPuja}
          style={{background:"none",border:"none",color:textCol,opacity:0.4,fontSize:13,
            cursor:"pointer",textDecoration:"underline"}}>
          संकल्प के बिना शुरू करें
        </button>
      </div>
    </div>
  );

  if (phase === "surrender") return (
    <div style={bg}>
      <IncenseSmoke color={deity.light}/>
      <div style={{maxWidth:440,margin:"0 auto",padding:"32px 20px",textAlign:"center"}}>
        <div className="vp-glow" style={{"--gl":deity.light}}>
          <DeityPortrait deityKey={deityKey} size={160} glowing/>
        </div>
        <h3 style={{color:textCol,fontSize:20,margin:"14px 0 6px"}}>समर्पण · Surrender</h3>
        <p style={{color:textCol,opacity:0.7,fontSize:14,margin:"0 0 14px"}}>आज {deity.hindi} को क्या समर्पित करना चाहते हैं?</p>
        <textarea value={surrender} onChange={e=>setSurrender(e.target.value)}
          placeholder="अपनी मनोकामना या संकल्प लिखें…" rows={3}
          style={{width:"100%",border:"1.5px solid rgba(255,255,255,0.3)",borderRadius:12,
            padding:"12px 14px",fontSize:15,background:"rgba(255,255,255,0.15)",color:textCol,
            resize:"none",outline:"none",boxSizing:"border-box",fontFamily:"inherit",backdropFilter:"blur(6px)"}}/>
        <button onClick={savePuja} disabled={saving}
          style={{marginTop:14,background:"rgba(255,255,255,0.2)",color:textCol,
            border:"1.5px solid rgba(255,255,255,0.4)",borderRadius:28,padding:"13px 36px",
            fontSize:17,fontWeight:700,cursor:saving?"default":"pointer",opacity:saving?0.7:1}}>
          {saving ? "सहेज रहे हैं…" : "🙏 पूजा सम्पन्न"}
        </button>
      </div>
    </div>
  );

  if (phase === "complete") return (
    <div style={bg}>
      <IncenseSmoke color={deity.light}/>
      <div style={{maxWidth:440,margin:"0 auto",padding:"44px 20px",textAlign:"center"}}>
        <div className="vp-glow" style={{"--gl":deity.light}}>
          <DeityPortrait deityKey={deityKey} size={190} glowing/>
        </div>
        <h2 style={{color:textCol,fontSize:24,margin:"16px 0 8px"}}>पूजा सम्पन्न 🙏</h2>
        <p style={{color:textCol,opacity:0.8,fontSize:16,margin:"0 0 24px",
          fontWeight:600,lineHeight:1.7}}>
          {deity.blessing}
        </p>
        <button onClick={()=>setPhase("intro")}
          style={{background:"rgba(255,255,255,0.18)",color:textCol,
            border:"1.5px solid rgba(255,255,255,0.4)",borderRadius:28,padding:"11px 28px",fontSize:15,cursor:"pointer"}}>
          ← वापस जाएं
        </button>
      </div>
    </div>
  );

  // ─── PUJA STEP ────────────────────────────────────────────────────────────
  const tappable = ["shower","auto","aarti-auto"].includes(step.interaction);
  return (
    <div style={bg} onClick={tappable ? handleTap : undefined} onTouchEnd={tappable ? handleTap : undefined}>
      {/* Hidden audio player for deity mantra — loops during puja */}
      <audio ref={audioRef} style={{display:"none"}} preload="none"/>
      <IncenseSmoke color={deity.light}/>
      {items.map(f => (
        <div key={f.id} className="vp-item-n" style={{
          left:f.x, top:f.y,
          animationDelay:`${f.delay||0}s`,
          fontSize:`${f.sz||28}px`,
          '--dx':`${f.dx||0}px`,
          '--fs': f.sz ? f.sz/28 : 1,
        }}>{f.e}</div>
      ))}

      <div style={{maxWidth:440,margin:"0 auto",padding:"14px 20px 24px",textAlign:"center"}}>
        {/* Nav */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <button onClick={(e)=>{e.stopPropagation();clearInterval(waveRef.current);setPhase("intro");}}
            style={{background:"rgba(255,255,255,0.15)",border:"none",color:textCol,fontSize:17,cursor:"pointer",padding:"5px 10px",borderRadius:10}}>←</button>
          <span style={{color:textCol,fontWeight:700,fontSize:14,opacity:0.85}}>{deity.hindi} पूजा</span>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <span style={{color:textCol,opacity:0.45,fontSize:12}}>{stepIdx+1}/{steps.length}</span>
            <button onClick={e=>{e.stopPropagation();setMuted(m=>!m);}}
              style={{background:"rgba(255,255,255,0.18)",border:"1px solid rgba(255,255,255,0.35)",
                color:textCol,fontSize:13,cursor:"pointer",padding:"3px 8px",borderRadius:20,
                lineHeight:1,display:"flex",alignItems:"center",gap:3}}>
              <span>{muted ? "🔇" : "🔊"}</span>
              <span style={{fontSize:11,opacity:0.85}}>{muted ? "Muted" : "Sound"}</span>
            </button>
          </div>
        </div>

        {/* Progress dots */}
        <div style={{display:"flex",gap:5,justifyContent:"center",marginBottom:10}}>
          {steps.map((s,i) => (
            <div key={s.id} style={{width:i===stepIdx?18:6,height:5,borderRadius:3,transition:"all 0.35s",
              background:i<stepIdx?"rgba(255,255,255,0.9)":i===stepIdx?"rgba(255,255,255,0.6)":"rgba(255,255,255,0.22)"}}/>
          ))}
        </div>

        {/* Temple bells — tappable, ring on demand */}
        <div style={{display:"flex",justifyContent:"center",gap:52,marginBottom:4,alignItems:"flex-start"}}>
          {[0,1].map(i => (
            <div key={i} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
              <div style={{width:2,height:14,background:`${deity.light}88`}}/>
              <button className="vp-bell"
                onClick={e=>{e.stopPropagation();playBell(0.4);}}
                style={{fontSize:20,filter:`drop-shadow(0 0 6px ${deity.light})`,
                  animationDelay:`${i*1.1}s`,background:"none",border:"none",
                  cursor:"pointer",padding:4,touchAction:"manipulation"}}>🔔</button>
            </div>
          ))}
        </div>

        {/* Deity portrait with divine radial aura + torana arch */}
        <div style={{position:"relative",display:"inline-block",marginBottom:4}}>
          {/* Divine radial rays behind deity */}
          <div style={{
            position:"absolute",top:"50%",left:"50%",
            width:300,height:300,marginLeft:-150,marginTop:-150,
            borderRadius:"50%",
            background:`radial-gradient(circle,${deity.light}28 0%,${deity.light}10 40%,transparent 70%)`,
            animation:"vpDivineRay 3s ease-in-out infinite",
            zIndex:0,
          }}/>
          {/* Torana top arch */}
          <div className="vp-torana" style={{
            position:"absolute",top:-22,left:"50%",transform:"translateX(-50%)",
            fontSize:24,letterSpacing:3,whiteSpace:"nowrap",
            color:"#FFB000",textShadow:`0 0 12px ${deity.light}`,
            zIndex:2,
          }}>🌼 ❀ 🌼</div>
          <div className={glowing ? "vp-glow" : ""} style={glowing ? {"--gl":deity.light,position:"relative",zIndex:1} : {position:"relative",zIndex:1}}>
            <DeityPortrait deityKey={deityKey} size={240} glowing={glowing}/>
          </div>
        </div>
        <div style={{fontSize:10,letterSpacing:4,color:textCol,opacity:0.45,textTransform:"uppercase",marginBottom:10}}>
          {deity.name}
        </div>

        {/* Sankalpa display — shown on first step only */}
        {sankalpa && stepIdx === 0 && (
          <div style={{fontSize:13,color:textCol,opacity:0.8,marginBottom:8,
            background:"rgba(255,255,255,0.12)",borderRadius:10,padding:"8px 14px",
            backdropFilter:"blur(6px)",border:"1px solid rgba(255,255,255,0.2)",
            lineHeight:1.6}}>
            🙏 संकल्प: <span style={{fontWeight:700}}>{sankalpa}</span>
          </div>
        )}

        {/* Mantra box */}
        <div key={`${stepIdx}-${mantraLine}`} className="vp-fadein"
          style={{fontSize:16,fontWeight:700,color:textCol,padding:"10px 16px",borderRadius:12,
            background:"rgba(255,255,255,0.13)",backdropFilter:"blur(6px)",
            marginBottom:8,lineHeight:1.6,letterSpacing:0.5,minHeight:50,
            display:"flex",alignItems:"center",justifyContent:"center",textAlign:"center"}}>
          {currentMantra}
        </div>

        {/* Step title */}
        <div style={{fontSize:20,fontWeight:800,color:textCol,margin:"4px 0 1px"}}>{step.hindi}</div>
        <div style={{fontSize:12,color:textCol,opacity:0.55,marginBottom:14}}>{step.desc}</div>

        {/* Auto / Aarti / Deepam */}
        {(step.interaction === "auto" || step.interaction === "aarti-auto") && (
          <div>
            {step.interaction === "aarti-auto"
              ? <AartiRing/>
              : (step.id === "deepam" || step.id === "tel")
              ? <DeepamWave/>
              : <div className="vp-flame" style={{fontSize:52,filter:`drop-shadow(0 0 20px ${deity.light})`}}>🪔</div>}
            <button onClick={e=>{e.stopPropagation();advanceStep(true);}}
              style={{marginTop:14,background:"rgba(255,255,255,0.18)",backdropFilter:"blur(6px)",
                color:textCol,border:"1.5px solid rgba(255,255,255,0.4)",borderRadius:24,
                padding:"10px 28px",fontSize:15,fontWeight:700,cursor:"pointer"}}>
              अगला →
            </button>
          </div>
        )}

        {/* Chalisa — manual advance, user reads at own pace */}
        {step.interaction === "manual" && (
          <div>
            <ChalisaSection textCol={textCol}/>
            <button onClick={e=>{e.stopPropagation();advanceStep(true);}}
              style={{marginTop:14,background:"rgba(255,255,255,0.18)",backdropFilter:"blur(6px)",
                color:textCol,border:"1.5px solid rgba(255,255,255,0.4)",borderRadius:24,
                padding:"12px 32px",fontSize:16,fontWeight:700,cursor:"pointer",
                display:"block",margin:"14px auto 0"}}>
              ✓ पाठ पूर्ण
            </button>
          </div>
        )}

        {/* Shower — before tap */}
        {step.interaction === "shower" && !done && (
          <div style={{paddingTop:4}}>
            <div className="vp-pulse" style={{fontSize:56,marginBottom:10,filter:`drop-shadow(0 0 18px ${deity.light})`}}>
              {(step.items||["🌸"])[0]}
            </div>
            <div style={{background:"rgba(255,255,255,0.18)",backdropFilter:"blur(6px)",color:textCol,
              borderRadius:26,padding:"13px 32px",fontSize:16,fontWeight:700,display:"inline-block",
              border:"1.5px solid rgba(255,255,255,0.4)"}}>
              एक बार स्पर्श करें
            </div>
          </div>
        )}

        {/* Shower — active, show timer + items */}
        {step.interaction === "shower" && done && secsLeft > 0 && (
          <div style={{paddingTop:6}}>
            <div style={{fontSize:36,marginBottom:6}}>{(step.items||["🌸"]).slice(0,4).join(" ")}</div>
            <div style={{fontSize:13,color:textCol,opacity:0.65}}>
              {secsLeft}s · {currentMantra.split("·")[0]?.trim()}
            </div>
            <button onClick={e=>{e.stopPropagation();clearInterval(waveRef.current);advanceStep(true);}}
              style={{background:"none",border:"none",color:textCol,opacity:0.35,
                fontSize:12,cursor:"pointer",marginTop:8,textDecoration:"underline"}}>
              छोड़ें →
            </button>
          </div>
        )}

        {/* Japa counter — tap to count mantra repetitions */}
        {step.interaction === "japa" && (
          <div>
            <div style={{background:"rgba(255,255,255,0.1)",backdropFilter:"blur(6px)",borderRadius:12,
              padding:"12px 16px",marginBottom:14,border:"1px solid rgba(255,255,255,0.18)"}}>
              {step.mantra.split("\n").map((line,i) => (
                <div key={i} style={{fontSize:15,fontWeight:600,color:textCol,fontFamily:"serif",
                  lineHeight:1.85,textAlign:"center"}}>{line}</div>
              ))}
            </div>
            {step.note && (
              <div style={{fontSize:12,color:textCol,opacity:0.7,marginBottom:12,
                fontStyle:"italic",lineHeight:1.6}}>
                ✦ {step.note}
              </div>
            )}
            <div style={{display:"flex",gap:8,justifyContent:"center",marginBottom:16}}>
              {[7,27,108].map(n => (
                <button key={n} onClick={e=>{e.stopPropagation();setJapaTarget(n);tapCountRef.current=0;setTapCount(0);}}
                  style={{background:japaTarget===n?"rgba(255,255,255,0.38)":"rgba(255,255,255,0.12)",
                    border:`1.5px solid ${japaTarget===n?"rgba(255,255,255,0.8)":"rgba(255,255,255,0.25)"}`,
                    color:textCol,borderRadius:20,padding:"5px 18px",fontSize:14,cursor:"pointer",
                    fontWeight:japaTarget===n?700:400}}>
                  {n}
                </button>
              ))}
            </div>
            <button onClick={e=>{e.stopPropagation();handleJapa();}}
              style={{width:128,height:128,borderRadius:"50%",
                background:`radial-gradient(circle,rgba(255,255,255,0.22) 0%,rgba(255,255,255,0.07) 100%)`,
                border:`2.5px solid rgba(255,255,255,${tapCount>0?0.8:0.4})`,
                color:textCol,cursor:"pointer",display:"flex",flexDirection:"column",
                alignItems:"center",justifyContent:"center",margin:"0 auto 10px",
                backdropFilter:"blur(8px)",touchAction:"manipulation",
                boxShadow:tapCount>0?`0 0 32px ${deity.light}70`:"none",
                transition:"all 0.2s"}}>
              <div style={{fontSize:24}}>🙏</div>
              <div style={{fontSize:32,fontWeight:900,lineHeight:1.1}}>{tapCount}</div>
              <div style={{fontSize:11,opacity:0.6}}>/ {japaTarget}</div>
            </button>
            <div style={{fontSize:12,color:textCol,opacity:0.45,marginTop:4}}>
              प्रत्येक जाप पर स्पर्श करें
            </div>
            <button onClick={e=>{e.stopPropagation();advanceStep(true);}}
              style={{background:"none",border:"none",color:textCol,opacity:0.35,
                fontSize:12,cursor:"pointer",marginTop:10,textDecoration:"underline"}}>
              छोड़ें →
            </button>
          </div>
        )}

        {/* One-tap — tap N times */}
        {step.interaction === "one-tap" && (
          <div style={{paddingTop:4}}>
            <button onClick={e=>{e.stopPropagation();handleOneTap();}}
              style={{background:"rgba(255,255,255,0.15)",backdropFilter:"blur(6px)",
                border:`1.5px solid rgba(255,255,255,${tapCount>0?0.75:0.4})`,
                borderRadius:20,padding:"20px 40px",cursor:"pointer",
                display:"block",margin:"0 auto 10px",touchAction:"manipulation",
                boxShadow:tapCount>0?`0 0 28px ${deity.light}60`:"none",
                transition:"all 0.15s"}}>
              <div className="vp-pulse" style={{fontSize:52,filter:`drop-shadow(0 0 18px ${deity.light})`}}>
                {(step.items||["💧"])[0]}
              </div>
            </button>
            <div style={{fontSize:15,fontWeight:700,color:textCol,marginBottom:2}}>
              {tapCount===0
                ? `${step.tapTarget||7} बार चढ़ाएं`
                : tapCount>=(step.tapTarget||7)
                ? "✓ अर्पण पूर्ण"
                : `${tapCount} / ${step.tapTarget||7}`}
            </div>
            <div style={{fontSize:12,color:textCol,opacity:0.45}}>हर बार स्पर्श करें</div>
          </div>
        )}

        {/* Long press pranama */}
        {step.interaction === "longpress" && (
          <div onClick={e=>e.stopPropagation()} onTouchEnd={e=>e.stopPropagation()}>
            <div style={{fontSize:14,fontWeight:700,color:textCol,marginBottom:10}}>
              नीचे दबाए रखें — प्रणाम पूर्ण होगा 🙏
            </div>
            <svg width={110} height={110}
              style={{cursor:"pointer",touchAction:"none",display:"block",margin:"0 auto"}}
              onMouseDown={startLP} onMouseUp={endLP} onMouseLeave={endLP}
              onTouchStart={startLP} onTouchEnd={endLP}>
              <circle cx={55} cy={55} r={42} fill="rgba(255,255,255,0.18)" stroke="rgba(255,255,255,0.3)" strokeWidth={2}/>
              <circle cx={55} cy={55} r={42} fill="none" stroke="rgba(255,255,255,0.88)" strokeWidth={7}
                strokeLinecap="round" strokeDasharray={LP_CIRC}
                strokeDashoffset={LP_CIRC*(1-lpPct/100)}
                style={{transition:"stroke-dashoffset 0.05s",transformOrigin:"55px 55px",transform:"rotate(-90deg)"}}/>
              <text x={55} y={63} textAnchor="middle" fontSize={28}>🙇</text>
            </svg>
            <button onClick={e=>{e.stopPropagation();advanceStep(true);}}
              style={{background:"none",border:"none",color:textCol,opacity:0.35,
                fontSize:12,cursor:"pointer",marginTop:12,textDecoration:"underline"}}>
              छोड़ें →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
